// Local Files Service
// Upload, manage, and queue local audio files

import { db, storage } from './firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
} from 'firebase/storage';
import { createRequest, type SongRequest } from './requests';

// ============================================
// TYPES
// ============================================

export interface DjLocalFile {
  id: string;
  djId: string;
  fileName: string;
  title: string;
  artist: string;
  storageUrl: string;
  durationMs: number;
  uploadedAt: string;
}

// ============================================
// AUDIO DURATION HELPER
// ============================================

export function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    const url = URL.createObjectURL(file);
    audio.addEventListener('loadedmetadata', () => {
      const durationMs = Math.round(audio.duration * 1000);
      URL.revokeObjectURL(url);
      resolve(durationMs);
    });
    audio.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read audio file'));
    });
    audio.src = url;
  });
}

// ============================================
// UPLOAD VALIDATION (mirrors storage.rules)
// ============================================

export const LOCAL_FILE_MAX_BYTES = 100 * 1024 * 1024;

const AUDIO_TYPES_BY_EXTENSION: Record<string, string> = {
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
};

export class LocalFileValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LocalFileValidationError';
  }
}

export function resolveAudioContentType(file: File): string | null {
  if (file.type.startsWith('audio/')) return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return AUDIO_TYPES_BY_EXTENSION[extension] ?? null;
}

export function validateLocalAudioFile(file: File): string | null {
  if (file.size > LOCAL_FILE_MAX_BYTES) return 'This file is larger than 100 MB.';
  if (!resolveAudioContentType(file)) return 'Only MP3, WAV, M4A and OGG audio files can be uploaded.';
  return null;
}

// ============================================
// UPLOAD
// ============================================

export async function uploadLocalFile(
  file: File,
  options: {
    eventId: string;
    djId: string;
    title: string;
    artist: string;
    temporary: boolean;
    durationMs: number;
  },
  onProgress?: (percent: number) => void
): Promise<{ storageUrl: string; fileDoc?: DjLocalFile }> {
  const validationError = validateLocalAudioFile(file);
  if (validationError) throw new LocalFileValidationError(validationError);
  const contentType = resolveAudioContentType(file) ?? undefined;

  const fileId = crypto.randomUUID();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');

  // Storage path based on temporary vs permanent
  const storagePath = options.temporary
    ? `events/${options.eventId}/localFiles/${fileId}_${safeName}`
    : `users/${options.djId}/localFiles/${fileId}_${safeName}`;

  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, file, { contentType });

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        onProgress?.(percent);
      },
      (error) => reject(error),
      async () => {
        try {
          const storageUrl = await getDownloadURL(uploadTask.snapshot.ref);

          // If permanent, save to DJ's local file library
          let fileDoc: DjLocalFile | undefined;
          if (!options.temporary) {
            const docRef = await addDoc(collection(db, `users/${options.djId}/localFiles`), {
              djId: options.djId,
              fileName: file.name,
              title: options.title,
              artist: options.artist,
              storageUrl,
              durationMs: options.durationMs,
              uploadedAt: new Date().toISOString(),
            });
            fileDoc = {
              id: docRef.id,
              djId: options.djId,
              fileName: file.name,
              title: options.title,
              artist: options.artist,
              storageUrl,
              durationMs: options.durationMs,
              uploadedAt: new Date().toISOString(),
            };
          }

          resolve({ storageUrl, fileDoc });
        } catch (err) {
          reject(err);
        }
      }
    );
  });
}

// ============================================
// DJ LOCAL FILE LIBRARY (permanent files)
// ============================================

export async function getDjLocalFiles(djId: string): Promise<DjLocalFile[]> {
  const q = query(
    collection(db, `users/${djId}/localFiles`),
    orderBy('uploadedAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as DjLocalFile));
}

export async function deleteDjLocalFile(djId: string, fileId: string, storageUrl: string): Promise<void> {
  // Delete Firestore doc
  await deleteDoc(doc(db, `users/${djId}/localFiles`, fileId));
  // Delete from Storage
  try {
    const storageRef = ref(storage, storageUrl);
    await deleteObject(storageRef);
  } catch {
    // Storage file may already be deleted
  }
}

// ============================================
// ADD TO QUEUE
// ============================================

export async function addLocalFileToQueue(
  eventId: string,
  djId: string,
  fileData: {
    storageUrl: string;
    fileName: string;
    title: string;
    artist: string;
    durationMs: number;
    temporary: boolean;
  },
  options: {
    visibleToAttendees: boolean;
    includeInPlaylist: boolean;
  }
): Promise<SongRequest> {
  const request = await createRequest(eventId, {
    song: {
      title: fileData.title,
      artist: fileData.artist,
      durationMs: fileData.durationMs,
      explicitFlag: false,
    },
    requestedBy: {
      oderId: djId,
      nickname: 'DJ',
    },
    initialStatus: 'queued',
    isLocalFile: true,
  });

  // Update with local file metadata (createRequest doesn't support these fields)
  const { updateRequest } = await import('./requests');
  await updateRequest(request.id, {
    localFile: {
      storageUrl: fileData.storageUrl,
      fileName: fileData.fileName,
      durationMs: fileData.durationMs,
      temporary: fileData.temporary,
    },
    visibleToAttendees: options.visibleToAttendees,
    includeInPlaylist: options.includeInPlaylist,
  });

  return {
    ...request,
    localFile: {
      storageUrl: fileData.storageUrl,
      fileName: fileData.fileName,
      durationMs: fileData.durationMs,
      temporary: fileData.temporary,
    },
    visibleToAttendees: options.visibleToAttendees,
    includeInPlaylist: options.includeInPlaylist,
  };
}

// ============================================
// CLEANUP
// ============================================

export async function cleanupTemporaryFiles(eventId: string): Promise<void> {
  try {
    const folderRef = ref(storage, `events/${eventId}/localFiles`);
    const result = await listAll(folderRef);
    await Promise.all(result.items.map(item => deleteObject(item)));
  } catch {
    // Folder may not exist or already cleaned
  }
}
